import { yupResolver } from "@hookform/resolvers/yup";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Empty } from "Components/_Shared/Empty";
import { Loader } from "Components/_Shared/Loader";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import {
  type CurrentSolutionWrapperFormType,
  getCurrentSolutionWrapperFormSchema,
} from "Forms/Schemas/GoalsFulfillment/CurrentSolution/currentSolutionWrapperFormSchema";
import {
  getGetAssetAllocationOverviewQueryKey,
  getListCurrentContractsQueryKey,
  useDeleteCurrentContract,
  useGetCurrentContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  ContractProposalBaseInvestmentToolType,
  CurrentContractBaseInvestmentToolType,
  type CurrentContractBaseSolutionHorizonType,
  type CurrentContractDetailCommodity,
  type CurrentContractDetailInvestment,
  type CurrentContractDetailOther,
  type CurrentContractDetailPensionSaving,
  type CurrentContractDetailSaving,
  type CurrentSolutionAssetOption,
} from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { CommoditiesForm } from "./CommoditiesForm";
import { CurrentSolutionPrefillAsset } from "./CurrentSolutionPrefillAsset";
import { InvestmentsForm } from "./InvestmentsForm";
import { OtherForm } from "./OtherForm";
import { PensionForm } from "./PensionForm";
import { SavingsForm } from "./SavingsForm";

type Props = {
  readonly edit?: boolean;
  readonly horizonType: CurrentContractBaseSolutionHorizonType;
  readonly solutionID?: string;
  readonly initialFormType?: ContractProposalBaseInvestmentToolType;
  readonly onSuccess?: () => void;
};

export type CurrentSolutionFormHandle = {
  readonly submitForm: () => void;
  readonly deleteSolution?: () => void;
  readonly prefillFromAssetOption?: (asset: CurrentSolutionAssetOption) => void;
};

export const CurrentSolutionWrapperForm = forwardRef<CurrentSolutionFormHandle, Props>(
  ({ edit, initialFormType = ContractProposalBaseInvestmentToolType.investment, horizonType, solutionID, onSuccess }, ref) => {
    const { t } = useResource();
    const { analysisId, versionId } = useContext(AnalysisDetailContext);
    const queryClient = useQueryClient();
    const innerFormRef = useRef<CurrentSolutionFormHandle>(null);

    const {
      control,
      watch,
      setValue,
      formState: { errors },
    } = useForm<CurrentSolutionWrapperFormType>({
      resolver: yupResolver(getCurrentSolutionWrapperFormSchema(t)),
      mode: "onBlur",
      defaultValues: {
        formType: initialFormType,
        institution: "",
      },
    });

    const formType = watch("formType");
    const institution = watch("institution");

    const { data: solutionData, isError: isSolutionError } = useGetCurrentContract(analysisId, versionId, solutionID || "", {
      query: { enabled: !!solutionID },
    });

    useEffect(() => {
      if (edit && solutionData) {
        setValue("institution", solutionData.partnerName ?? "");
      }
    }, [edit, solutionData, setValue]);

    const formTypeOptions: Option[] = (
      Object.keys(CurrentContractBaseInvestmentToolType) as Array<keyof typeof CurrentContractBaseInvestmentToolType>
    ).map((value) => ({
      value,
      name: t(Resources.AnalysisDetail.GoalsFulfillment.InvestmentToolTypeOptions[value]),
    }));

    const { mutate: deleteMutation } = useDeleteCurrentContract();

    const deleteSolution = () => {
      if (!edit || !solutionID) return;
      deleteMutation(
        { analysisId, versionId, currentContractGuid: solutionID },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: getListCurrentContractsQueryKey(analysisId, versionId),
            });
            queryClient.invalidateQueries({
              queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId),
            });
            onSuccess?.();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
    };

    const [pendingAsset, setPendingAsset] = useState<CurrentSolutionAssetOption | null>(null);

    const handleAssetPick = (asset: CurrentSolutionAssetOption) => {
      setValue("institution", asset.institution, { shouldValidate: true, shouldDirty: true });
      setValue("formType", asset.investmentToolType, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setPendingAsset(asset);
    };

    useEffect(() => {
      if (!pendingAsset) return;
      if (pendingAsset.investmentToolType !== formType) return;

      innerFormRef.current?.prefillFromAssetOption?.(pendingAsset);
      setPendingAsset(null);
    }, [pendingAsset, formType]);

    useImperativeHandle(ref, () => ({
      submitForm: () => innerFormRef.current?.submitForm(),
      deleteSolution: () => deleteSolution(),
    }));

    if (edit && !solutionData) {
      return isSolutionError ? (
        <Empty
          title={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Title)}
          text={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Text)}
          icon={<ErrorOutlineIcon sx={{ fontSize: "40px", color: "grey.500" }} />}
        />
      ) : (
        <Loader />
      );
    }

    return (
      <>
        <form>
          <Stack spacing={Spacing.XXL} mb={Spacing.XXL}>
            <Box>
              <Grid container spacing={Spacing.XL}>
                {!edit && (
                  <Grid size={12}>
                    <CurrentSolutionPrefillAsset onAssetPick={handleAssetPick} />
                  </Grid>
                )}

                <Grid size={4}>
                  <FormDropdown
                    name="formType"
                    options={formTypeOptions}
                    control={control}
                    labelEndAdornment={<StyledLabelAdornment />}
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.InvestmentToolTypeOptions.Title)}
                    errors={errors}
                    disabled={edit}
                  />
                </Grid>

                <Grid size={8}>
                  <BlFormInput
                    control={control}
                    name="institution"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Institution)}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </form>

        {formType === CurrentContractBaseInvestmentToolType.investment && (
          <InvestmentsForm
            investmentToolType={formType}
            institution={institution}
            horizonType={horizonType}
            edit={edit}
            currentSolutionId={solutionID}
            initialData={solutionData as CurrentContractDetailInvestment}
            ref={innerFormRef}
            onSuccess={onSuccess}
          />
        )}
        {formType === CurrentContractBaseInvestmentToolType.commodity && (
          <CommoditiesForm
            investmentToolType={formType}
            institution={institution}
            horizonType={horizonType}
            edit={edit}
            currentSolutionId={solutionID}
            initialData={solutionData as CurrentContractDetailCommodity}
            ref={innerFormRef}
            onSuccess={onSuccess}
          />
        )}
        {formType === CurrentContractBaseInvestmentToolType.pension_saving && (
          <PensionForm
            investmentToolType={formType}
            institution={institution}
            horizonType={horizonType}
            edit={edit}
            currentSolutionId={solutionID}
            initialData={solutionData as CurrentContractDetailPensionSaving}
            ref={innerFormRef}
            onSuccess={onSuccess}
          />
        )}
        {formType === CurrentContractBaseInvestmentToolType.saving && (
          <SavingsForm
            investmentToolType={formType}
            institution={institution}
            horizonType={horizonType}
            edit={edit}
            currentSolutionId={solutionID}
            initialData={solutionData as CurrentContractDetailSaving}
            ref={innerFormRef}
            onSuccess={onSuccess}
          />
        )}
        {formType === CurrentContractBaseInvestmentToolType.other && (
          <OtherForm
            investmentToolType={formType}
            institution={institution}
            horizonType={horizonType}
            edit={edit}
            currentSolutionId={solutionID}
            initialData={solutionData as CurrentContractDetailOther}
            ref={innerFormRef}
            onSuccess={onSuccess}
          />
        )}
      </>
    );
  },
);
